import type { UserData, MessageTrigger, DomainEntry, ScheduledMessage, Task } from './types';

const STORAGE_KEYS = {
  TRIGGERS: 'telegram_triggers',
  DOMAINS: 'customDomains',
  CHAT_TAGS: 'chat_tags',
  AVAILABLE_TAGS: 'available_tags',
  SHOW_RED_MARKS: 'showRedMarks',
  RED_MARK_TIMESTAMPS: 'red_mark_timestamps',
  DOMAIN_RED_MARKS: 'domain_red_marks',
  GLOBAL_DELAY: 'domainRedMarkDelay',
  WORKING_HOURS: 'working_hours_cache',
  SCHEDULED_MESSAGES: 'scheduled_messages',
  TASKS: 'tasks',
};

// Helper functions for localStorage operations
function getItem<T>(key: string, defaultValue: T): T {
  const item = localStorage.getItem(key);
  if (!item) return defaultValue;
  try {
    return JSON.parse(item);
  } catch {
    return defaultValue;
  }
}

function setItem<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

// Triggers
export function getTriggers(): MessageTrigger[] {
  return getItem<MessageTrigger[]>(STORAGE_KEYS.TRIGGERS, []);
}

export function addTrigger(trigger: Omit<MessageTrigger, 'id' | 'createdAt' | 'notificationCount'>): MessageTrigger {
  const triggers = getTriggers();
  const newTrigger: MessageTrigger = {
    ...trigger,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    notificationCount: 0,
  };
  setItem(STORAGE_KEYS.TRIGGERS, [...triggers, newTrigger]);
  return newTrigger;
}

export function updateTrigger(triggerId: string, updates: Partial<MessageTrigger>): MessageTrigger | null {
  const triggers = getTriggers();
  const updatedTriggers = triggers.map(trigger => 
    trigger.id === triggerId ? { ...trigger, ...updates } : trigger
  );
  setItem(STORAGE_KEYS.TRIGGERS, updatedTriggers);
  return updatedTriggers.find(t => t.id === triggerId) || null;
}

export function deleteTrigger(triggerId: string): boolean {
  const triggers = getTriggers();
  const updatedTriggers = triggers.filter(trigger => trigger.id !== triggerId);
  setItem(STORAGE_KEYS.TRIGGERS, updatedTriggers);
  return true;
}

// Domains
export function getDomains(): DomainEntry[] {
  return getItem<DomainEntry[]>(STORAGE_KEYS.DOMAINS, []);
}

export function addDomain(domain: Omit<DomainEntry, 'id' | 'createdAt'>): DomainEntry {
  const domains = getDomains();
  const newDomain: DomainEntry = {
    ...domain,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };
  setItem(STORAGE_KEYS.DOMAINS, [...domains, newDomain]);
  return newDomain;
}

export function updateDomain(domainId: string, updates: Partial<DomainEntry>): DomainEntry | null {
  const domains = getDomains();
  const updatedDomains = domains.map(domain => 
    domain.id === domainId ? { ...domain, ...updates } : domain
  );
  setItem(STORAGE_KEYS.DOMAINS, updatedDomains);
  return updatedDomains.find(d => d.id === domainId) || null;
}

export function deleteDomain(domainId: string): boolean {
  const domains = getDomains();
  const updatedDomains = domains.filter(domain => domain.id !== domainId);
  setItem(STORAGE_KEYS.DOMAINS, updatedDomains);
  return true;
}

// Scheduled Messages
export function getScheduledMessages(): ScheduledMessage[] {
  return getItem<ScheduledMessage[]>(STORAGE_KEYS.SCHEDULED_MESSAGES, []);
}

export function addScheduledMessage(message: Omit<ScheduledMessage, 'id'>): ScheduledMessage {
  const messages = getScheduledMessages();
  const newMessage: ScheduledMessage = {
    ...message,
    id: crypto.randomUUID(),
  };
  setItem(STORAGE_KEYS.SCHEDULED_MESSAGES, [...messages, newMessage]);
  return newMessage;
}

export function updateScheduledMessage(messageId: string, updates: Partial<ScheduledMessage>): ScheduledMessage | null {
  const messages = getScheduledMessages();
  const updatedMessages = messages.map(message => 
    message.id === messageId ? { ...message, ...updates } : message
  );
  setItem(STORAGE_KEYS.SCHEDULED_MESSAGES, updatedMessages);
  return updatedMessages.find(m => m.id === messageId) || null;
}

export function deleteScheduledMessage(messageId: string): boolean {
  const messages = getScheduledMessages();
  const updatedMessages = messages.filter(message => message.id !== messageId);
  setItem(STORAGE_KEYS.SCHEDULED_MESSAGES, updatedMessages);
  return true;
}

// Tasks
export function getTasks(): Task[] {
  return getItem<Task[]>(STORAGE_KEYS.TASKS, []);
}

export function addTask(task: Omit<Task, 'id' | 'createdAt'>): Task {
  const tasks = getTasks();
  const newTask: Task = {
    ...task,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };
  setItem(STORAGE_KEYS.TASKS, [...tasks, newTask]);
  return newTask;
}

export function updateTask(taskId: string, updates: Partial<Task>): Task | null {
  const tasks = getTasks();
  const updatedTasks = tasks.map(task => 
    task.id === taskId ? { ...task, ...updates } : task
  );
  setItem(STORAGE_KEYS.TASKS, updatedTasks);
  return updatedTasks.find(t => t.id === taskId) || null;
}

export function deleteTask(taskId: string): boolean {
  const tasks = getTasks();
  const updatedTasks = tasks.filter(task => task.id !== taskId);
  setItem(STORAGE_KEYS.TASKS, updatedTasks);
  return true;
}

// Tags
export function getChatTags(): Record<string, string[]> {
  return getItem<Record<string, string[]>>(STORAGE_KEYS.CHAT_TAGS, {});
}

export function getAvailableTags(): string[] {
  return getItem<string[]>(STORAGE_KEYS.AVAILABLE_TAGS, []);
}

export function updateChatTags(chatId: string, tags: string[]): void {
  const chatTags = getChatTags();
  setItem(STORAGE_KEYS.CHAT_TAGS, { ...chatTags, [chatId]: tags });
}

export function addAvailableTag(tag: string): void {
  const availableTags = getAvailableTags();
  if (!availableTags.includes(tag)) {
    setItem(STORAGE_KEYS.AVAILABLE_TAGS, [...availableTags, tag]);
  }
}

export function removeAvailableTag(tag: string): void {
  const availableTags = getAvailableTags();
  setItem(STORAGE_KEYS.AVAILABLE_TAGS, availableTags.filter(t => t !== tag));
  // Also remove from all chat tags
  const chatTags = getChatTags();
  const updatedChatTags = Object.fromEntries(
    Object.entries(chatTags).map(([chatId, tags]) => [chatId, tags.filter(t => t !== tag)])
  );
  setItem(STORAGE_KEYS.CHAT_TAGS, updatedChatTags);
}

// Red Marks
export function getRedMarks(): {
  showRedMarks: boolean;
  timestamps: Record<string, number>;
  domainMarks: Record<string, boolean>;
} {
  return {
    showRedMarks: localStorage.getItem(STORAGE_KEYS.SHOW_RED_MARKS) === 'true',
    timestamps: getItem<Record<string, number>>(STORAGE_KEYS.RED_MARK_TIMESTAMPS, {}),
    domainMarks: getItem<Record<string, boolean>>(STORAGE_KEYS.DOMAIN_RED_MARKS, {}),
  };
}

export function updateRedMarks(updates: Partial<{
  showRedMarks: boolean;
  timestamps: Record<string, number>;
  domainMarks: Record<string, boolean>;
}>): void {
  const current = getRedMarks();
  const updated = { ...current, ...updates };
  
  localStorage.setItem(STORAGE_KEYS.SHOW_RED_MARKS, String(updated.showRedMarks));
  setItem(STORAGE_KEYS.RED_MARK_TIMESTAMPS, updated.timestamps);
  setItem(STORAGE_KEYS.DOMAIN_RED_MARKS, updated.domainMarks);
}

// Global Delay
export function getGlobalDelay(): { days: number; hours: number; minutes: number } {
  return getItem<{ days: number; hours: number; minutes: number }>(
    STORAGE_KEYS.GLOBAL_DELAY,
    { days: 0, hours: 0, minutes: 0 }
  );
}

export function updateGlobalDelay(delay: { days: number; hours: number; minutes: number }): void {
  setItem(STORAGE_KEYS.GLOBAL_DELAY, delay);
}

// Working Hours
export function getWorkingHours(): {
  enabled: boolean;
  excludeWeekends: boolean;
  excludeNonWorkingHours: boolean;
  startTime: string;
  endTime: string;
} {
  return getItem<{
    enabled: boolean;
    excludeWeekends: boolean;
    excludeNonWorkingHours: boolean;
    startTime: string;
    endTime: string;
  }>(STORAGE_KEYS.WORKING_HOURS, {
    enabled: false,
    excludeWeekends: false,
    excludeNonWorkingHours: false,
    startTime: '12:00',
    endTime: '18:00'
  });
}

export function updateWorkingHours(workingHours: {
  enabled: boolean;
  excludeWeekends: boolean;
  excludeNonWorkingHours: boolean;
  startTime: string;
  endTime: string;
}): void {
  setItem(STORAGE_KEYS.WORKING_HOURS, workingHours);
} 